import { t } from "i18next";
import React, { useEffect, useState } from "react";
import { Form, Table, Button, Modal } from "react-bootstrap";
import { Training, emptyTraining } from "../../../../interfaces/training";
import { getTrainingsBySeason, createTraining, updateTraining, deleteTraining } from "../../../../services/training";
import { TrainingSection, emptyTrainingSection } from "../../../../interfaces/training-section";
import { createTrainingSection, updateTrainingSection, deleteTrainingSection } from "../../../../services/training-section";
import { LoadingSpinner } from "../../../general/loading-spinner";
import { planningLevels } from "../../../../interfaces/planning";
import { Season } from "../../../../interfaces/season";
import { getSeasonOptions, getSeasonCurrent } from "../../../../services/season";

export function CoachTrainingsList() {
    const [sections, setSections] = useState<TrainingSection[]>([]);
    const [trainings, setTrainings] = useState<Training[]>([]);
    const [collapsedSections, setCollapsedSections] = useState<Set<number>>(new Set());
    const [seasons, setSeasons] = useState<Season[]>([]);
    const [selectedSeasonId, setSelectedSeasonId] = useState<number>(0);
    const [editingId, setEditingId] = useState<number | null>(null);
    const [editingSectionId, setEditingSectionId] = useState<number | null>(null);
    const [deletingId, setDeletingId] = useState<number | null>(null);
    const [addingSectionAfterIndex, setAddingSectionAfterIndex] = useState<number | null>(null);
    const [addingTrainingForSection, setAddingTrainingForSection] = useState<number | null>(null);
    const [loaded, setLoaded] = useState(false);
    const [editFormData, setEditFormData] = useState<Training>(emptyTraining);
    const [editSectionFormData, setEditSectionFormData] = useState<TrainingSection>(emptyTrainingSection);
    const [newSectionData, setNewSectionData] = useState<TrainingSection>(emptyTrainingSection);
    const [newTrainingData, setNewTrainingData] = useState<Training>(emptyTraining);
    const [showCloneModal, setShowCloneModal] = useState(false);
    const [cloneSourceSeasonId, setCloneSourceSeasonId] = useState<number>(0);
    const [cloning, setCloning] = useState(false);
    const [searchText, setSearchText] = useState<string>('');
    const [showScrollTop, setShowScrollTop] = useState(false);

    // Helper function to get value for a specific key from training values
    const getValueForKey = (training: Training, key: string): string => {
        if (!training.values) return '';
        const value = training.values.find(v => v.value_key === key);
        return value ? value.value : '';
    };

    // Helper function to format numeric values with decimals (using comma as decimal separator for Spanish)
    const formatNumericValue = (value: number | string | undefined | null): string => {
        if (value === undefined || value === null || value === '' || value === 0 || value === '0') return '0';
        // Parse the value to ensure it's a number and format with 1 decimal place
        const numValue = typeof value === 'string' ? parseFloat(value.replace(',', '.')) : value;
        if (isNaN(numValue) || numValue === 0) return '0';
        // Use comma as decimal separator for Spanish format
        return numValue.toFixed(1).replace('.', ',');
    };

    // Helper function to populate km, th, codigo_extra and nota from values array
    const populateExtraFieldsFromValues = (training: Training): Training => {
        const values = training.values || [];

        // Buscar primero en mayúsculas (como vienen del endpoint), luego en minúsculas como fallback
        let km = values.find(v => v.value_key === 'KM');
        if (!km) km = values.find(v => v.value_key === 'km');

        let th = values.find(v => v.value_key === 'TH');
        if (!th) th = values.find(v => v.value_key === 'th');

        const codigo = values.find(v => v.value_key === 'codigo');
        const nota = values.find(v => v.value_key === 'nota');

        return {
            ...training,
            id: typeof training.id === 'string' ? parseInt(training.id) : training.id,
            section_id: typeof training.section_id === 'string' ? parseInt(training.section_id) : training.section_id,
            values: values,
            km: km ? parseFloat(km.value) : 0,
            th: th ? parseFloat(th.value) : 0,
            codigo_extra: codigo ? String(codigo.value || '') : '',
            nota: nota ? String(nota.value || '') : ''
        };
    };

    /**
     * Loads sections and trainings using the optimized endpoint
     * This makes a single API call instead of N+1 calls (one for sections + one per section for trainings)
     * Performance improvement: 3 SQL queries total vs potentially dozens with the old approach
     */
    const loadSectionsAndTrainings = async (seasonId: number) => {
        try {
            // Use optimized endpoint that returns sections with nested trainings
            const response = await getTrainingsBySeason(seasonId);

            // Process sections data
            const sectionsData = response.data.map((s: any) => ({
                id: typeof s.id === 'string' ? parseInt(s.id) : s.id,
                season_id: seasonId,
                name: s.name,
                order: typeof s.order === 'string' ? parseInt(s.order) : s.order,
                collapsed: false
            }));

            setSections(sectionsData);

            // Extract all trainings from the nested structure
            const allTrainings: Training[] = [];
            response.data.forEach((section: any) => {
                if (section.trainings && Array.isArray(section.trainings)) {
                    const sectionTrainings = section.trainings.map((t: any) =>
                        populateExtraFieldsFromValues({
                            ...t,
                            section_id: section.id
                        })
                    );
                    allTrainings.push(...sectionTrainings);
                }
            });

            setTrainings(allTrainings);
            setLoaded(true);
        } catch (error) {
            setLoaded(true);
        }
    };

    // Helper function to update value for a specific key in new training mode
    const updateNewTrainingValueForKey = (key: string, value: string) => {
        const existingValueIndex = newTrainingData.values.findIndex(v => v.value_key === key);
        const newValues = [...newTrainingData.values];

        if (existingValueIndex >= 0) {
            newValues[existingValueIndex] = {
                ...newValues[existingValueIndex],
                value: value
            };
        } else {
            newValues.push({
                id: 0,
                training_id: 0,
                value_key: key,
                value: value,
                created_by: 0,
                created_at: '',
                changed_by: 0,
                changed_at: ''
            });
        }

        setNewTrainingData({
            ...newTrainingData,
            values: newValues
        });
    };

    // Helper function to update value for a specific key in edit mode
    const updateValueForKey = (key: string, value: string) => {
        const existingValueIndex = editFormData.values.findIndex(v => v.value_key === key);
        const newValues = [...editFormData.values];

        if (existingValueIndex >= 0) {
            newValues[existingValueIndex] = {
                ...newValues[existingValueIndex],
                value: value
            };
        } else {
            newValues.push({
                id: 0,
                training_id: editFormData.id,
                value_key: key,
                value: value,
                created_by: 0,
                created_at: '',
                changed_by: 0,
                changed_at: ''
            });
        }

        setEditFormData({...editFormData, values: newValues});
    };

    // Get trainings for a specific section with optional search filtering
    const getTrainingsForSection = (sectionId: number): Training[] => {
        let sectionTrainings = trainings.filter(t => t.section_id === sectionId);

        // Apply search filter if search text exists
        if (searchText.trim() !== '') {
            const searchLower = searchText.toLowerCase().trim();
            sectionTrainings = sectionTrainings.filter(t => {
                const tipoMatch = t.tipo && t.tipo.toLowerCase().includes(searchLower);
                const descripcionMatch = t.descripcion && t.descripcion.toLowerCase().includes(searchLower);
                return tipoMatch || descripcionMatch;
            });
        }

        return sectionTrainings;
    };

    useEffect(() => {
        Promise.all([
            getSeasonOptions(),
            getSeasonCurrent()
        ]).then(response => {
            const seasonsList = response[0].data;
            const currentSeason = response[1].data;

            setSeasons(seasonsList);

            if (currentSeason && currentSeason.id) {
                setSelectedSeasonId(currentSeason.id);
                loadSectionsAndTrainings(currentSeason.id);
            } else if (seasonsList.length > 0) {
                setSelectedSeasonId(seasonsList[0].id);
                loadSectionsAndTrainings(seasonsList[0].id);
            } else {
                setLoaded(true);
            }
        })
        .catch(error => {
            console.log(error);
            setLoaded(true);
        });
    }, []);

    // Handle scroll to show/hide scroll to top button
    useEffect(() => {
        const handleScroll = () => {
            if (window.pageYOffset > 300) {
                setShowScrollTop(true);
            } else {
                setShowScrollTop(false);
            }
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const scrollToTop = () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleSeasonChange = (event: any) => {
        const seasonId = parseInt(event.target.value);
        setSelectedSeasonId(seasonId);
        setLoaded(false);
        loadSectionsAndTrainings(seasonId);
    }

    const handleMoveSectionUp = (index: number) => {
        if (index === 0) return; // Can't move first item up

        const newSections = [...sections];
        [newSections[index - 1], newSections[index]] = [newSections[index], newSections[index - 1]];

        // Update order property for all sections
        const updatedSections = newSections.map((section, idx) => ({
            ...section,
            order: idx
        }));

        setSections(updatedSections);

        // Update each section's order in the backend
        updatedSections.forEach(section => {
            updateTrainingSection({
                id: section.id,
                season_id: section.season_id,
                name: section.name,
                order: section.order
            }).catch(error => {
                console.log('Error updating section order:', error);
            });
        });
    };

    const handleMoveSectionDown = (index: number) => {
        if (index === sections.length - 1) return; // Can't move last item down

        const newSections = [...sections];
        [newSections[index], newSections[index + 1]] = [newSections[index + 1], newSections[index]];

        // Update order property for all sections
        const updatedSections = newSections.map((section, idx) => ({
            ...section,
            order: idx
        }));

        setSections(updatedSections);

        // Update each section's order in the backend
        updatedSections.forEach(section => {
            updateTrainingSection({
                id: section.id,
                season_id: section.season_id,
                name: section.name,
                order: section.order
            }).catch(error => {
                console.log('Error updating section order:', error);
            });
        });
    };

    // Section handlers
    const handleToggleSection = (sectionId: number) => {
        setCollapsedSections(prev => {
            const newSet = new Set(prev);
            if (newSet.has(sectionId)) {
                newSet.delete(sectionId);
            } else {
                newSet.add(sectionId);
            }
            return newSet;
        });
    };

    const handleAddSectionClick = (afterIndex: number) => {
        setAddingSectionAfterIndex(afterIndex);
        setNewSectionData({
            ...emptyTrainingSection,
            season_id: selectedSeasonId,
            order: afterIndex + 1
        });
    };

    const handleCancelAddSection = () => {
        setAddingSectionAfterIndex(null);
        setNewSectionData(emptyTrainingSection);
    };

    const handleSaveNewSection = () => {
        if (!newSectionData.name || newSectionData.name.trim() === '') {
            alert(t("Please fill in section name"));
            return;
        }

        const body = {
            season_id: selectedSeasonId,
            name: newSectionData.name,
            order: newSectionData.order
        };

        createTrainingSection(body)
            .then((response: any) => {
                if (response.data.status == 1) {
                    const newSection = {
                        ...response.data.data,
                        id: typeof response.data.data.id === 'string' ? parseInt(response.data.data.id) : response.data.data.id,
                        collapsed: false
                    };

                    // Insert section at the correct position
                    const newSections = [...sections];
                    newSections.splice(addingSectionAfterIndex! + 1, 0, newSection);
                    setSections(newSections);

                    setAddingSectionAfterIndex(null);
                    setNewSectionData(emptyTrainingSection);
                } else {
                    alert(response.data.message);
                }
            })
            .catch((error: any) => {
                console.log(error);
                alert(t("Error creating section"));
            });
    };

    const handleEditSection = (sectionId: number) => {
        const section = sections.find(s => s.id === sectionId);
        if (section) {
            setEditingSectionId(sectionId);
            setEditSectionFormData({...section});
        }
    };

    const handleSaveEditSection = () => {
        if (!editSectionFormData.name || editSectionFormData.name.trim() === '') {
            alert(t("Please fill in section name"));
            return;
        }

        const body = {
            id: editSectionFormData.id,
            season_id: editSectionFormData.season_id,
            name: editSectionFormData.name,
            order: editSectionFormData.order
        };

        updateTrainingSection(body)
            .then((response: any) => {
                if (response.data.status == 1) {
                    const newSections = sections.map(s => {
                        if (s.id === editSectionFormData.id) {
                            return {
                                ...response.data.data,
                                id: typeof response.data.data.id === 'string' ? parseInt(response.data.data.id) : response.data.data.id,
                                collapsed: s.collapsed
                            };
                        }
                        return s;
                    });
                    setSections(newSections);
                    setEditingSectionId(null);
                    setEditSectionFormData(emptyTrainingSection);
                } else {
                    alert(response.data.message);
                }
            })
            .catch((error: any) => {
                console.log(error);
                alert(t("Error updating section"));
            });
    };

    const handleCancelEditSection = () => {
        setEditingSectionId(null);
        setEditSectionFormData(emptyTrainingSection);
    };

    const handleDeleteSection = (sectionId: number) => {
        setTimeout(() => {
            if (window.confirm(t("delete_section_confirm"))) {
                deleteTrainingSection(sectionId)
                    .then(response => {
                        if (response.data !== 0) {
                            const newSections = sections.filter(s => s.id !== sectionId);
                            setSections(newSections);
                            // Note: trainings in this section will have section_id = 0 or remain
                            // Backend should handle this appropriately
                        } else {
                            alert(t("Section cannot be deleted"));
                        }
                    })
                    .catch(error => {
                        console.log(error);
                        alert(t("Error deleting section"));
                    });
            }
        }, 100);
    };

    // Training handlers
    const handleDelete = (event: any) => {
        event.preventDefault();
        const id = parseInt(event.currentTarget.dataset.id);

        setDeletingId(id);

        setTimeout(() => {
            if (window.confirm(t("Are you sure you want to delete this training?"))) {
                deleteTraining(id)
                    .then(response => {
                        if (response.data !== 0) {
                            const newTrainings = trainings.filter((m: Training) => m.id !== id);
                            setTrainings(newTrainings);
                        } else {
                            alert(t("Training cannot be deleted"));
                        }
                        setDeletingId(null);
                    })
                    .catch(error => {
                        console.log(error);
                        alert(t("Error deleting training"));
                        setDeletingId(null);
                    });
            } else {
                setDeletingId(null);
            }
        }, 100);
    }

    const handleEdit = (event: any) => {
        event.preventDefault();
        const id = parseInt(event.currentTarget.dataset.id);
        const training = trainings.find(t => t.id === id);

        if (training) {
            setEditingId(id);
            setEditFormData({...populateExtraFieldsFromValues(training)});
        }
    }

    const handleSaveEdit = (event: any) => {
        event.preventDefault();

        // Preparar los valores incluyendo km, th, codigo_extra y nota
        const allValues = [...editFormData.values.filter(v =>
            v.value_key !== 'KM' &&
            v.value_key !== 'km' &&
            v.value_key !== 'TH' &&
            v.value_key !== 'th' &&
            v.value_key !== 'codigo' &&
            v.value_key !== 'nota'
        )];

        // Añadir KM si tiene valor (usar mayúsculas para consistencia con el endpoint)
        if (editFormData.km && editFormData.km > 0) {
            allValues.push({
                id: 0,
                training_id: editFormData.id,
                value_key: 'KM',
                value: editFormData.km.toString(),
                created_by: 0,
                created_at: '',
                changed_by: 0,
                changed_at: ''
            });
        }

        // Añadir TH si tiene valor (usar mayúsculas para consistencia con el endpoint)
        if (editFormData.th && editFormData.th > 0) {
            allValues.push({
                id: 0,
                training_id: editFormData.id,
                value_key: 'TH',
                value: editFormData.th.toString(),
                created_by: 0,
                created_at: '',
                changed_by: 0,
                changed_at: ''
            });
        }

        // Añadir código_extra si tiene valor
        if (editFormData.codigo_extra && String(editFormData.codigo_extra).trim() !== '') {
            allValues.push({
                id: 0,
                training_id: editFormData.id,
                value_key: 'codigo',
                value: String(editFormData.codigo_extra),
                created_by: 0,
                created_at: '',
                changed_by: 0,
                changed_at: ''
            });
        }

        // Añadir nota si tiene valor
        if (editFormData.nota && String(editFormData.nota).trim() !== '') {
            allValues.push({
                id: 0,
                training_id: editFormData.id,
                value_key: 'nota',
                value: String(editFormData.nota),
                created_by: 0,
                created_at: '',
                changed_by: 0,
                changed_at: ''
            });
        }

        const body = {
            id: editFormData.id,
            section_id: editFormData.section_id,
            codigo: editFormData.codigo || '',
            tipo: editFormData.tipo,
            carga: editFormData.carga,
            descripcion: editFormData.descripcion,
            comentarios: editFormData.comentarios || '',
            values: allValues.filter(v => v.value && v.value.toString().trim() !== '')
        };

        updateTraining(body)
            .then((response: any) => {
                if (response.data.status == 1) {
                    const newTrainings = trainings.map((m: Training) => {
                        if (m.id === editFormData.id) {
                            return populateExtraFieldsFromValues(response.data.data);
                        }
                        return m;
                    });
                    setTrainings(newTrainings);
                    setEditingId(null);
                    setEditFormData(emptyTraining);
                } else {
                    alert(response.data.message);
                }
            })
            .catch((error: any) => {
                console.log(error);
                alert(t("Error updating training"));
            });
    }

    const handleCancelEdit = (event: any) => {
        event.preventDefault();
        setEditingId(null);
        setEditFormData(emptyTraining);
    }

    const handleAddTraining = (sectionId: number) => {
        setAddingTrainingForSection(sectionId);
        setNewTrainingData({
            ...emptyTraining,
            section_id: sectionId
        });
    };

    const handleSaveNewTraining = () => {
        // Validar campos obligatorios
        if (!newTrainingData.tipo || !newTrainingData.descripcion) {
            alert(t("Please fill in all required fields"));
            return;
        }

        // Preparar los valores incluyendo km, th, codigo_extra y nota
        const allValues = [];

        // Añadir valores de planning levels
        planningLevels.forEach(level => {
            const value = getValueForKey(newTrainingData, level.key);
            if (value && value.toString().trim() !== '') {
                allValues.push({
                    id: 0,
                    training_id: 0,
                    value_key: level.key,
                    value: value,
                    created_by: 0,
                    created_at: '',
                    changed_by: 0,
                    changed_at: ''
                });
            }
        });

        // Añadir KM si tiene valor (usar mayúsculas para consistencia con el endpoint)
        if (newTrainingData.km && newTrainingData.km > 0) {
            allValues.push({
                id: 0,
                training_id: 0,
                value_key: 'KM',
                value: newTrainingData.km.toString(),
                created_by: 0,
                created_at: '',
                changed_by: 0,
                changed_at: ''
            });
        }

        // Añadir TH si tiene valor (usar mayúsculas para consistencia con el endpoint)
        if (newTrainingData.th && newTrainingData.th > 0) {
            allValues.push({
                id: 0,
                training_id: 0,
                value_key: 'TH',
                value: newTrainingData.th.toString(),
                created_by: 0,
                created_at: '',
                changed_by: 0,
                changed_at: ''
            });
        }

        // Añadir código_extra si tiene valor
        if (newTrainingData.codigo_extra && String(newTrainingData.codigo_extra).trim() !== '') {
            allValues.push({
                id: 0,
                training_id: 0,
                value_key: 'codigo',
                value: String(newTrainingData.codigo_extra),
                created_by: 0,
                created_at: '',
                changed_by: 0,
                changed_at: ''
            });
        }

        // Añadir nota si tiene valor
        if (newTrainingData.nota && String(newTrainingData.nota).trim() !== '') {
            allValues.push({
                id: 0,
                training_id: 0,
                value_key: 'nota',
                value: String(newTrainingData.nota),
                created_by: 0,
                created_at: '',
                changed_by: 0,
                changed_at: ''
            });
        }

        const body = {
            section_id: newTrainingData.section_id,
            codigo: newTrainingData.codigo || '',
            tipo: newTrainingData.tipo,
            carga: newTrainingData.carga,
            descripcion: newTrainingData.descripcion,
            comentarios: newTrainingData.comentarios || '',
            values: allValues
        };

        createTraining(body)
            .then((response: any) => {
                if (response.data.status == 1) {
                    const newTrainings = [...trainings, populateExtraFieldsFromValues(response.data.data)];
                    setTrainings(newTrainings);
                    setAddingTrainingForSection(null);
                    setNewTrainingData(emptyTraining);
                } else {
                    alert(response.data.message);
                }
            })
            .catch((error: any) => {
                console.log(error);
                alert(t("Error creating training"));
            });
    };

    const handleCancelAddTraining = () => {
        setAddingTrainingForSection(null);
        setNewTrainingData(emptyTraining);
    };

    // Clone trainings from another season
    const handleCloneTrainings = async () => {
        if (!cloneSourceSeasonId || cloneSourceSeasonId === selectedSeasonId) {
            alert("Por favor, selecciona una temporada diferente para copiar");
            return;
        }

        if (!window.confirm("ATENCIÓN: Esto borrará todos los entrenamientos actuales de esta temporada y los reemplazará con los de la temporada seleccionada. ¿Continuar?")) {
            return;
        }

        setCloning(true);

        try {
            const token = localStorage.getItem('token');
            const headers = {
                'Authorization': 'Bearer ' + token,
                'Content-Type': 'application/json'
            };

            const body = {
                source_season_id: cloneSourceSeasonId,
                target_season_id: selectedSeasonId
            };

            const endpointUrl = process.env.REACT_APP_API_BASE_URL + '/wp-json/padeltech/ws/v1/training/copy-season';

            const response = await fetch(endpointUrl, {
                method: 'POST',
                headers: headers,
                body: JSON.stringify(body)
            });

            const data = await response.json();

            if (data.status === "1" || data.status === 1) {
                // Reload the current season's data
                await loadSectionsAndTrainings(selectedSeasonId);
                setShowCloneModal(false);
                setCloneSourceSeasonId(0);

                const sectionsCopied = data.data?.sections_copied || 0;
                alert(`Entrenamientos copiados correctamente. ${sectionsCopied} secciones copiadas.`);
            } else {
                alert(data.message || "Error al copiar entrenamientos");
            }
        } catch (error) {
            console.error('Error cloning trainings:', error);
            alert("Error al copiar entrenamientos");
        } finally {
            setCloning(false);
        }
    };

    // Render training row
    const renderTrainingRow = (training: Training, isCollapsed: boolean) => {
        const isEditing = editingId === training.id;
        const isBlurred = (editingId !== null && editingId !== training.id) ||
                         (deletingId !== null && deletingId !== training.id) ||
                         (editingSectionId !== null) ||
                         (addingTrainingForSection !== null);
        const rowClass = isEditing ? 'editing' : (isBlurred ? 'blurred' : '');

        if (isCollapsed) return null;

        return (
            <tr key={training.id} className={rowClass}>
                <td className="sticky-col sticky-actions">
                    {isEditing ? (
                        <>
                            <a href="#" className="icon-link save me-2" title={t("Save")} onClick={handleSaveEdit} data-id={training.id}>
                                <i className="bi bi-check-circle"></i>
                            </a>
                            <a href="#" className="icon-link cancel" title={t("Cancel")} onClick={handleCancelEdit}>
                                <i className="bi bi-x-circle"></i>
                            </a>
                        </>
                    ) : (
                        <>
                            <a href="#" className="icon-link edit me-2" title={t("Edit")} onClick={handleEdit} data-id={training.id}>
                                <i className="bi bi-pencil-square"></i>
                            </a>
                            <a href="#" className="icon-link delete" title={t("Delete")} onClick={handleDelete} data-id={training.id}>
                                <i className="bi bi-trash"></i>
                            </a>
                        </>
                    )}
                </td>
                <td className="sticky-col sticky-type">
                    {isEditing ? (
                        <Form.Control
                            size="sm"
                            type="text"
                            value={editFormData.tipo}
                            onChange={(e) => setEditFormData({...editFormData, tipo: e.target.value})}
                        />
                    ) : (
                        training.tipo
                    )}
                </td>
                <td className="col-carga">
                    {isEditing ? (
                        <Form.Control
                            size="sm"
                            type="number"
                            value={editFormData.carga}
                            onChange={(e) => setEditFormData({...editFormData, carga: parseFloat(e.target.value) || 0})}
                        />
                    ) : (
                        training.carga
                    )}
                </td>
                <td className="col-descripcion">
                    {isEditing ? (
                        <Form.Control
                            size="sm"
                            type="text"
                            value={editFormData.descripcion}
                            onChange={(e) => setEditFormData({...editFormData, descripcion: e.target.value})}
                        />
                    ) : (
                        training.descripcion
                    )}
                </td>
                {planningLevels.map((level) => (
                    <td key={`${training.id}-${level.key}`} className="col-planning-level">
                        {isEditing ? (
                            <Form.Control
                                size="sm"
                                type="number"
                                step="0.1"
                                value={getValueForKey(editFormData, level.key)}
                                onChange={(e) => updateValueForKey(level.key, e.target.value)}
                            />
                        ) : (
                            formatNumericValue(getValueForKey(training, level.key))
                        )}
                    </td>
                ))}
                <td className="col-km">
                    {isEditing ? (
                        <Form.Control
                            size="sm"
                            type="number"
                            step="0.1"
                            value={editFormData.km || ''}
                            onChange={(e) => setEditFormData({...editFormData, km: parseFloat(e.target.value) || 0})}
                        />
                    ) : (
                        formatNumericValue(training.km)
                    )}
                </td>
                <td className="col-th">
                    {isEditing ? (
                        <Form.Control
                            size="sm"
                            type="number"
                            step="0.1"
                            value={editFormData.th || ''}
                            onChange={(e) => setEditFormData({...editFormData, th: parseFloat(e.target.value) || 0})}
                        />
                    ) : (
                        formatNumericValue(training.th)
                    )}
                </td>
                <td className="col-codigo-extra">
                    {isEditing ? (
                        <Form.Control
                            size="sm"
                            type="text"
                            value={editFormData.codigo_extra}
                            onChange={(e) => setEditFormData({...editFormData, codigo_extra: e.target.value})}
                        />
                    ) : (
                        training.codigo_extra
                    )}
                </td>
                <td className="col-nota">
                    {isEditing ? (
                        <Form.Control
                            size="sm"
                            type="text"
                            value={editFormData.nota}
                            onChange={(e) => setEditFormData({...editFormData, nota: e.target.value})}
                        />
                    ) : (
                        training.nota
                    )}
                </td>
            </tr>
        );
    };

    // Render section row
    const renderSectionRow = (section: TrainingSection, index: number) => {
        const isCollapsed = collapsedSections.has(section.id);
        const isEditing = editingSectionId === section.id;
        const sectionTrainings = getTrainingsForSection(section.id);
        const colSpan = 4 + planningLevels.length + 4; // actions + type + carga + descripcion + planning levels + km + th + codigo + nota

        // Don't render section if it has no trainings and search is active
        if (searchText.trim() !== '' && sectionTrainings.length === 0) {
            return null;
        }

        return (
            <React.Fragment>
                {/* Section header row */}
                <tr
                    id={`section-${section.id}`}
                    key={`section-${section.id}`}
                    className={isEditing ? "section-row section-editing" : "section-row"}
                >
                    <td colSpan={4} className="sticky-section">
                        {isEditing ? (
                            <div className="section-header">
                                <Form.Control
                                    size="sm"
                                    type="text"
                                    value={editSectionFormData.name}
                                    onChange={(e) => setEditSectionFormData({...editSectionFormData, name: e.target.value})}
                                    style={{ maxWidth: '250px' }}
                                    onClick={(e) => e.stopPropagation()}
                                />
                                <Button variant="success" size="sm" onClick={(e) => { e.stopPropagation(); handleSaveEditSection(); }}>
                                    <i className="bi bi-check-circle"></i>
                                </Button>
                                <Button variant="secondary" size="sm" onClick={(e) => { e.stopPropagation(); handleCancelEditSection(); }}>
                                    <i className="bi bi-x-circle"></i>
                                </Button>
                            </div>
                        ) : (
                            <div className="section-header">
                                <i className={`bi bi-chevron-down collapse-icon ${isCollapsed ? 'collapsed' : ''}`} onClick={() => handleToggleSection(section.id)} style={{ cursor: 'pointer' }}></i>
                                <div className="section-name" onClick={() => handleToggleSection(section.id)} style={{ cursor: 'pointer' }}>
                                    {section.name} <span className="training-count">({sectionTrainings.length})</span>
                                </div>
                                <div className="section-actions">
                                    <Button
                                        variant="link"
                                        size="sm"
                                        onClick={(e) => { e.stopPropagation(); handleMoveSectionUp(index); }}
                                        title={t("move_up")}
                                        disabled={index === 0}
                                    >
                                        <i className="bi bi-arrow-up"></i>
                                    </Button>
                                    <Button
                                        variant="link"
                                        size="sm"
                                        onClick={(e) => { e.stopPropagation(); handleMoveSectionDown(index); }}
                                        title={t("move_down")}
                                        disabled={index === sections.length - 1}
                                    >
                                        <i className="bi bi-arrow-down"></i>
                                    </Button>
                                    <Button
                                        variant="link"
                                        size="sm"
                                        onClick={(e) => { e.stopPropagation(); handleAddTraining(section.id); }}
                                        title={t("new_training")}
                                    >
                                        <i className="bi bi-plus-circle"></i>
                                    </Button>
                                    <Button
                                        variant="link"
                                        size="sm"
                                        onClick={(e) => { e.stopPropagation(); handleEditSection(section.id); }}
                                        title={t("edit_section")}
                                    >
                                        <i className="bi bi-pencil-square"></i>
                                    </Button>
                                    <Button
                                        variant="link"
                                        size="sm"
                                        onClick={(e) => { e.stopPropagation(); handleDeleteSection(section.id); }}
                                        title={t("delete_section")}
                                    >
                                        <i className="bi bi-trash"></i>
                                    </Button>
                                </div>
                            </div>
                        )}
                    </td>
                    <td colSpan={colSpan - 4}></td>
                </tr>

                {/* New training row form - appears first in section */}
                {addingTrainingForSection === section.id && !isCollapsed && (
                    <tr className="new-row">
                        <td className="sticky-col sticky-actions">
                            <a href="#" className="icon-link save me-2" title={t("Save")} onClick={(e) => { e.preventDefault(); handleSaveNewTraining(); }}>
                                <i className="bi bi-check-circle"></i>
                            </a>
                            <a href="#" className="icon-link cancel" title={t("Cancel")} onClick={(e) => { e.preventDefault(); handleCancelAddTraining(); }}>
                                <i className="bi bi-x-circle"></i>
                            </a>
                        </td>
                        <td className="sticky-col sticky-type">
                            <Form.Control
                                size="sm"
                                type="text"
                                value={newTrainingData.tipo}
                                onChange={(e) => setNewTrainingData({...newTrainingData, tipo: e.target.value})}
                            />
                        </td>
                        <td className="col-carga">
                            <Form.Control
                                size="sm"
                                type="number"
                                value={newTrainingData.carga || ''}
                                onChange={(e) => setNewTrainingData({...newTrainingData, carga: parseInt(e.target.value) || 0})}
                            />
                        </td>
                        <td className="col-descripcion">
                            <Form.Control
                                size="sm"
                                type="text"
                                value={newTrainingData.descripcion}
                                onChange={(e) => setNewTrainingData({...newTrainingData, descripcion: e.target.value})}
                            />
                        </td>
                        {planningLevels.map((level) => (
                            <td key={level.key} className="col-planning-level">
                                <Form.Control
                                    size="sm"
                                    type="number"
                                    value={getValueForKey(newTrainingData, level.key)}
                                    onChange={(e) => updateNewTrainingValueForKey(level.key, e.target.value)}
                                />
                            </td>
                        ))}
                        <td className="col-km">
                            <Form.Control
                                size="sm"
                                type="number"
                                step="0.1"
                                value={newTrainingData.km || ''}
                                onChange={(e) => setNewTrainingData({...newTrainingData, km: parseFloat(e.target.value) || 0})}
                            />
                        </td>
                        <td className="col-th">
                            <Form.Control
                                size="sm"
                                type="number"
                                step="0.1"
                                value={newTrainingData.th || ''}
                                onChange={(e) => setNewTrainingData({...newTrainingData, th: parseFloat(e.target.value) || 0})}
                            />
                        </td>
                        <td className="col-codigo-extra">
                            <Form.Control
                                size="sm"
                                type="text"
                                value={newTrainingData.codigo_extra || ''}
                                onChange={(e) => setNewTrainingData({...newTrainingData, codigo_extra: e.target.value})}
                            />
                        </td>
                        <td className="col-nota">
                            <Form.Control
                                size="sm"
                                type="text"
                                value={newTrainingData.nota || ''}
                                onChange={(e) => setNewTrainingData({...newTrainingData, nota: e.target.value})}
                            />
                        </td>
                    </tr>
                )}

                {/* Training rows for this section - appear after new training form */}
                {sectionTrainings.map(training => renderTrainingRow(training, isCollapsed))}

            </React.Fragment>
        );
    };

    return (
        <div className="trainings-list-container">
            <h1>
                { t('trainings')}
            </h1>
            { !loaded && <LoadingSpinner /> }
            { loaded &&
                <>
                    <div className="filters-container">
                        <Form>
                            <Form.Group controlId="season">
                                <Form.Select
                                    value={ selectedSeasonId }
                                    onChange={ handleSeasonChange }
                                    aria-label={ t("season") }
                                >
                                    {seasons.map((season) => (
                                        <option key={season.id} value={season.id}>
                                            {season.name}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                            <Form.Group controlId="search" className="ms-2" style={{ minWidth: '300px' }}>
                                <Form.Control
                                    type="text"
                                    placeholder={t("search_by_type_description")}
                                    value={searchText}
                                    onChange={(e) => setSearchText(e.target.value)}
                                />
                            </Form.Group>
                            <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => setShowCloneModal(true)}
                                className="ms-2"
                            >
                                <i className="bi bi-copy me-1"></i>
                                Copiar de otra temporada
                            </Button>
                        </Form>
                    </div>

                    {/* Sections index */}
                    {sections.length > 0 && searchText.trim() === '' && (
                        <div className="sections-index">
                            <h5>{t("Sections Index")}</h5>
                            <div className="sections-links">
                                {sections.map((section, index) => (
                                    <a
                                        key={section.id}
                                        href={`#section-${section.id}`}
                                        className="section-link"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            const element = document.getElementById(`section-${section.id}`);
                                            if (element) {
                                                const yOffset = -180; // Offset to account for sticky index height
                                                const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
                                                window.scrollTo({ top: y, behavior: 'smooth' });
                                            }
                                        }}
                                    >
                                        {section.name}
                                    </a>
                                ))}
                            </div>
                        </div>
                    )}
                </>
            }
            {loaded &&
                <div className="table-responsive">
                    <Table striped bordered hover size="sm">
                        <thead>
                            <tr>
                                <th className="sticky-col sticky-actions"></th>
                                <th className="sticky-col sticky-type">{ t("type") }</th>
                                <th className="col-carga">{ t("load") }</th>
                                <th className="col-descripcion">{ t("description") }</th>
                                {planningLevels.map((level) => (
                                    <th key={level.key} className="col-planning-level">{level.name}</th>
                                ))}
                                <th className="col-km">{ t("km") }</th>
                                <th className="col-th">{ t("th") }</th>
                                <th className="col-codigo-extra">{ t("code_extra") }</th>
                                <th className="col-nota">{ t("note") }</th>
                            </tr>
                        </thead>
                        <tbody>
                            {sections.length === 0 ? (
                                <>
                                    <tr className="add-section-row">
                                        <td colSpan={4 + planningLevels.length + 4}>
                                            <Button
                                                variant="link"
                                                size="sm"
                                                className="add-section-btn"
                                                onClick={() => handleAddSectionClick(-1)}
                                            >
                                                <i className="bi bi-plus-circle me-1"></i>
                                                {t("add_section")}
                                            </Button>
                                        </td>
                                    </tr>
                                    {/* Add section form when no sections exist */}
                                    {addingSectionAfterIndex === -1 && (
                                        <tr className="section-row section-editing">
                                            <td colSpan={4 + planningLevels.length + 4}>
                                                <div className="section-header">
                                                    <Form.Control
                                                        size="sm"
                                                        type="text"
                                                        placeholder={t("section_name")}
                                                        value={newSectionData.name}
                                                        onChange={(e) => setNewSectionData({...newSectionData, name: e.target.value})}
                                                        style={{ maxWidth: '250px' }}
                                                    />
                                                    <Button variant="success" size="sm" onClick={handleSaveNewSection}>
                                                        <i className="bi bi-check-circle"></i>
                                                    </Button>
                                                    <Button variant="secondary" size="sm" onClick={handleCancelAddSection}>
                                                        <i className="bi bi-x-circle"></i>
                                                    </Button>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </>
                            ) : (
                                <>
                                    {sections.map((section, index) => (
                                        <React.Fragment key={`section-${section.id}`}>
                                            {renderSectionRow(section, index)}
                                        </React.Fragment>
                                    ))}

                                    {/* Add section button at the end of all sections */}
                                    <tr className="add-section-row">
                                        <td colSpan={4 + planningLevels.length + 4}>
                                            <Button
                                                variant="link"
                                                size="sm"
                                                className="add-section-btn"
                                                onClick={() => handleAddSectionClick(sections.length - 1)}
                                            >
                                                <i className="bi bi-plus-circle me-1"></i>
                                                {t("add_section")}
                                            </Button>
                                        </td>
                                    </tr>

                                    {/* Add section form at the end */}
                                    {addingSectionAfterIndex === sections.length - 1 && (
                                        <tr className="section-row section-editing">
                                            <td colSpan={4 + planningLevels.length + 4}>
                                                <div className="section-header">
                                                    <Form.Control
                                                        size="sm"
                                                        type="text"
                                                        placeholder={t("section_name")}
                                                        value={newSectionData.name}
                                                        onChange={(e) => setNewSectionData({...newSectionData, name: e.target.value})}
                                                        style={{ maxWidth: '250px' }}
                                                    />
                                                    <Button variant="success" size="sm" onClick={handleSaveNewSection}>
                                                        <i className="bi bi-check-circle"></i>
                                                    </Button>
                                                    <Button variant="secondary" size="sm" onClick={handleCancelAddSection}>
                                                        <i className="bi bi-x-circle"></i>
                                                    </Button>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </>
                            )}
                        </tbody>
                    </Table>
                </div>
            }

            {/* Clone trainings modal */}
            <Modal show={showCloneModal} onHide={() => setShowCloneModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Copiar de otra temporada</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Group>
                        <Form.Label>Selecciona la temporada origen</Form.Label>
                        <Form.Select
                            value={cloneSourceSeasonId}
                            onChange={(e) => setCloneSourceSeasonId(parseInt(e.target.value))}
                        >
                            <option value={0}>Selecciona una temporada</option>
                            {seasons.filter(s => {
                                const seasonId = typeof s.id === 'string' ? parseInt(s.id) : s.id;
                                return seasonId !== selectedSeasonId;
                            }).map((season) => (
                                <option key={season.id} value={season.id}>
                                    {season.name}
                                </option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                    <p className="mt-3 text-danger small">
                        <i className="bi bi-exclamation-triangle me-1"></i>
                        <strong>Atención:</strong> Se borrarán todos los entrenamientos actuales de esta temporada y se copiarán todas las secciones y entrenamientos de la temporada seleccionada.
                    </p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowCloneModal(false)} disabled={cloning}>
                        Cancelar
                    </Button>
                    <Button variant="primary" onClick={handleCloneTrainings} disabled={cloning || !cloneSourceSeasonId}>
                        {cloning ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                Copiando...
                            </>
                        ) : (
                            <>
                                <i className="bi bi-copy me-1"></i>
                                Copiar
                            </>
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Scroll to top button */}
            {showScrollTop && (
                <button
                    className="scroll-to-top-btn"
                    onClick={scrollToTop}
                    title={t("Back to top")}
                >
                    <i className="bi bi-arrow-up"></i>
                </button>
            )}
        </div>
    );
}
